"use client";

import {
  createContext,
  useContext,
  useMemo,
  type ReactNode,
} from "react";

import { useSession, type TenantSettings } from "@/hooks/useSession";

export interface TenantContext {
  /** Organisation / tenant ID */
  tenantId: string | undefined;
  /** Human-readable organisation name */
  name: string | null | undefined;
  /** URL-safe slug */
  slug: string | null | undefined;
  /** Current billing plan */
  plan: string | null | undefined;
  /** Organisation status (active, suspended, etc.) */
  status: string | null | undefined;
  /** Billing account ID */
  accountId: string | undefined;
  /** Feature services enabled for this tenant */
  enabledServices: string[];
  /** Full tenant settings (theme, map, alerts, etc.) */
  settings: TenantSettings | null;
  /** Whether the tenant context has been fully loaded */
  isReady: boolean;
  /** Quick helper: is a given service enabled? */
  hasService: (service: string) => boolean;
}

const TenantCtx = createContext<TenantContext>({
  tenantId: undefined,
  name: undefined,
  slug: undefined,
  plan: undefined,
  status: undefined,
  accountId: undefined,
  enabledServices: [],
  settings: null,
  isReady: false,
  hasService: () => false,
});

export function TenantProvider({ children }: { children: ReactNode }) {
  const session = useSession();

  const value = useMemo<TenantContext>(() => {
    const services = session.enabledServices ?? [];

    return {
      tenantId: session.tenantId,
      name: session.tenantName,
      slug: session.tenantSlug,
      plan: session.tenantPlan,
      status: session.tenantStatus,
      accountId: session.accountId,
      enabledServices: services,
      settings: session.tenantSettings ?? null,
      isReady: session.status === "ready" && !!session.tenantId,
      hasService: (service: string) => services.includes(service),
    };
  }, [
    session.tenantId,
    session.tenantName,
    session.tenantSlug,
    session.tenantPlan,
    session.tenantStatus,
    session.accountId,
    session.enabledServices,
    session.tenantSettings,
    session.status,
  ]);

  return <TenantCtx.Provider value={value}>{children}</TenantCtx.Provider>;
}

export function useTenant(): TenantContext {
  return useContext(TenantCtx);
}
